import type { components } from "@/api/types";
import { NIOSH_REL_PPM, pctRel, type RelGas } from "@/lib/carcinogen";

export type Role =
  | "respai_admin"
  | "respai_sales"
  | "chief"
  | "incident_commander"
  | "firefighter";

export type Department = components["schemas"]["DepartmentRead"] & {
  address?: string | null;
  phone?: string | null;
  // Presigned by /auth/me when the department has a logo (RES-159).
  logo_url?: string | null;
  // Also from /auth/me. Describes the VIEWER's tenant only — the printed
  // report's demonstration banner comes from the report itself (RES-402).
  environment_class?: string | null;
};
export type DepartmentUpdate = components["schemas"]["DepartmentUpdate"];

export type CurrentUser = components["schemas"]["UserRead"] & {
  role: Role;
  roles: Role[];
  display_name: string;
  department: Department | null;
  // The caller's real home department id from /auth/me, even for a platform admin
  // (whose tenant `department_id` is null). Lets the UI recognise its own internal
  // tenant, e.g. the "RespAI Internal" tab where admin invites are offered.
  home_department_id: string | null;
};

export type DepartmentOverview = {
  kpis: {
    incidents_this_month: number;
    sensor_minutes_recorded: number;
    peak_gas_exposures: number;
    // Every incident whose readings make up `peak_gas_exposures`, most
    // contributors first, so the KPI drills down to the exact incidents that add
    // up to its number — each row opens its incident detail. `count` is the
    // number of over-REL readings that incident contributed.
    peak_gas_incidents: Array<{
      incident_id: string;
      label: string;
      count: number;
      dispatched_at: string;
    }>;
    open_incidents: number;
  };
  // Carcinogen-exposure load per calendar month for the trailing window (oldest
  // first), including zero months so the bar chart keeps an even axis. Each month
  // COUNTS the firefighter-exposures whose PEAK reading crossed a carcinogen
  // screening limit: `co` is the number whose CO peak crossed the NIOSH CO REL
  // screen (35 ppm) — the incomplete-combustion marker — and `voc` the number
  // whose VOC peak crossed the NIOSH benzene STEL screen (1 ppm) — the conservative
  // aromatic-carcinogen screen. These are peak-vs-limit screens, not confirmed
  // dose exceedances. The chart stacks them so a chief sees the full combustion-
  // carcinogen burden, not CO alone (a count, not a summed %REL, keeps the unit
  // honest — see src/lib/carcinogen.ts).
  monthly_trends: Array<{
    month: string; // "YYYY-MM"
    label: string; // short month label, e.g. "Jun"
    co: number; // exposures whose CO peak crossed the NIOSH CO REL screen that month
    voc: number; // exposures whose VOC peak crossed the benzene STEL screen that month
    // De-duplicated count of exposures flagged by EITHER screen (CO ∪ VOC) — a record
    // over both is counted once. This is the figure the headline + chart height
    // report, so they match the "Peak gas exposures" KPI; `co`/`voc` overlap and
    // must not be summed.
    total: number;
  }>;
  recent_activity: Array<{
    id: string;
    label: string;
    ts: string;
    link?: string;
  }>;
};

export type IncidentStatus = "active" | "cleared" | "reviewed" | "finalized";

export type IncidentSummary = components["schemas"]["IncidentRead"] & {
  status: IncidentStatus;
  sensor_wearer_display_name?: string | null;
  alerts_count?: number;
};

export type IncidentPage = {
  items: IncidentSummary[];
  total: number;
  page: number;
  page_size: number;
};

export type IncidentDetail = IncidentSummary & {
  ic_display_name?: string | null;
  sensor_device_serial?: string | null;
};

export type IncidentEvent = components["schemas"]["EventRead"] & {
  kind: string;
  ts: string;
  actor_display_name?: string | null;
};

export type GasReading = components["schemas"]["GasReadingRead"] & {
  gas: string;
  value_ppm?: number | null;
  alert_level?: "none" | "low" | "high" | "stel" | "twa";
};

export type ExposureTotal = {
  gas: string;
  ppm_minutes: number;
  peak_ppm: number;
  niosh_rel_ppm?: number | null;
  // Peak as a % of the gas's NIOSH REL, computed once via pctRel (the single
  // source of truth) so every consumer renders the same figure; over_rel is just
  // pct_rel ≥ 100.
  pct_rel?: number | null;
  over_rel?: boolean;
};

export type ExposureRecord = components["schemas"]["ExposureRecordRead"] & {
  totals: ExposureTotal[];
  pdf_url?: string | null;
};

export type IncidentReportV1 = {
  hazards_identified: string[];
  decon_actions: string[];
  gaps: string[];
  after_action_notes: string;
  [key: string]: unknown;
};

export type IncidentReportDraft = components["schemas"]["ReportRead"] & {
  body: IncidentReportV1;
  markdown?: string | null;
  pdf_url?: string | null;
};

export type Job = {
  id?: string;
  job_id?: string;
  status: "queued" | "running" | "succeeded" | "failed" | string;
  result_url?: string | null;
  error_message?: string | null;
  [key: string]: unknown;
};

export type JobRef = {
  job_id: string;
};

export type AuditLogEntry = {
  id: string;
  ts: string;
  actor_user_id?: string | null;
  actor_display_name?: string | null;
  action: string;
  target_type?: string | null;
  target_id?: string | null;
  ip?: string | null;
};

export function toAuditLogEntry(
  row: components["schemas"]["AuditLogRead"],
): AuditLogEntry {
  return {
    id: row.id,
    ts: row.occurred_at,
    actor_user_id: row.actor_user_id ?? null,
    // The API resolves the actor UUID to app_user.full_name (department-scoped);
    // null for system actions or actors outside the caller's visible scope.
    actor_display_name: row.actor_display_name ?? null,
    action: row.action,
    target_type: row.resource_type ?? null,
    target_id: row.resource_id ?? null,
    ip: row.ip_address ?? null,
  };
}

export type UserSummary = components["schemas"]["UserRead"] & {
  role: Role;
  roles: Role[];
  display_name: string;
  sensor_minutes_recorded?: number;
  last_incident_at?: string | null;
};

export type UserDetail = UserSummary;

export type UserExposureHistory = {
  totals: ExposureTotal[];
  incidents: Array<{
    incident_id: string;
    dispatched_at: string;
    ppm_minutes_total?: number;
  }>;
};

export type UserInvite = {
  email: string;
  given_name: string;
  family_name: string;
  roles: Role[];
};

export function toDisplayName(user: components["schemas"]["UserRead"]): string {
  return user.full_name || user.email;
}

export function isRole(value: string): value is Role {
  return (
    value === "chief" ||
    value === "respai_admin" ||
    value === "respai_sales" ||
    value === "incident_commander" ||
    value === "firefighter"
  );
}

export function toUserSummary(
  user: components["schemas"]["UserRead"],
): UserSummary {
  const rawRoles =
    "roles" in user && Array.isArray(user.roles) ? user.roles : [user.role];
  const roles = rawRoles.filter((role): role is Role => isRole(String(role)));
  return {
    ...user,
    role: isRole(user.role) ? user.role : "firefighter",
    roles:
      roles.length > 0
        ? roles
        : [isRole(user.role) ? user.role : "firefighter"],
    display_name: toDisplayName(user),
    sensor_minutes_recorded: 0,
    last_incident_at: null,
  };
}

export function toDepartment(
  department: components["schemas"]["DepartmentRead"],
): Department {
  return {
    ...department,
    address: department.jurisdiction,
    phone: null,
  };
}

const INCIDENT_STATUSES: readonly IncidentStatus[] = [
  "active",
  "cleared",
  "reviewed",
  "finalized",
];

export function toIncidentSummary(
  incident: components["schemas"]["IncidentRead"],
): IncidentSummary {
  // Surface the real backend lifecycle (active → cleared → reviewed → finalized);
  // unknown values fall back to "active" so an incident is never silently hidden.
  const status = (INCIDENT_STATUSES as readonly string[]).includes(
    incident.status,
  )
    ? (incident.status as IncidentStatus)
    : "active";
  return {
    ...incident,
    status,
    // Enrichment derived server-side from the attribution chain + alerts; the
    // API now returns these, so carry them through instead of blanking them.
    sensor_wearer_display_name: incident.sensor_wearer_display_name ?? null,
    alerts_count: incident.alerts_count ?? 0,
  };
}

/** "residential_structure" → "Residential Structure"; null/blank → null. */
export function humanizeIncidentType(
  type: string | null | undefined,
): string | null {
  if (!type) return null;
  const words = type
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length > 0 ? words.join(" ") : null;
}

/**
 * Human label for an incident: prefer the type + address (what responders
 * recognise — "Residential Structure · 12 Burn St") over the opaque UUID.
 * Falls back to the CAD incident number, then a short id, so a row is never
 * blank.
 */
export function incidentDisplayName(incident: {
  id: string;
  incident_type?: string | null;
  address?: string | null;
  incident_number?: string | null;
}): string {
  const type = humanizeIncidentType(incident.incident_type);
  const address = incident.address?.trim() || null;
  if (type && address) return `${type} · ${address}`;
  if (address) return address;
  if (type) return type;
  if (incident.incident_number) return incident.incident_number;
  return `Incident ${incident.id.slice(0, 8)}`;
}

export function toGasReadings(
  reading: components["schemas"]["GasReadingRead"],
): GasReading[] {
  const measured = reading.measured_at;
  const gases = [
    ["CO", reading.co_ppm],
    ["H2S", reading.h2s_ppm],
    ["VOC", reading.voc_ppm],
    ["LEL", reading.lel_pct],
  ] as const;

  return gases.flatMap(([gas, raw]) => {
    if (raw === undefined || raw === null) return [];
    const value = Number(raw);
    return [
      {
        ...reading,
        id: `${reading.id}-${gas}`,
        gas,
        measured_at: measured,
        value_ppm: Number.isFinite(value) ? value : null,
        alert_level: reading.alarm_state ? "high" : "none",
      },
    ];
  });
}

/**
 * Map the `/v1/users/{id}/exposure-history` response (career dose totals + an
 * incident timeline) onto the front-end's `UserExposureHistory`. The API reports
 * measured quantities only — cumulative dose (Σ `<gas>_ppm_min`) and the worst
 * peak per gas — so we layer the NIOSH-REL context on each gas total here via
 * `pctRel` (one source of truth, src/lib/carcinogen.ts) to match the per-incident
 * table and overview KPI to the digit.
 *
 * `ppm_minutes` is now the firefighter's real cumulative dose, not the `0` the old
 * client-side stub fabricated; `ppm_minutes_total` per incident is the additive
 * CO+H2S+VOC dose the backend already summed (LEL excluded — %LEL·min isn't a
 * ppm·min carcinogen dose).
 */
export function toUserExposureHistory(
  raw: components["schemas"]["UserExposureHistory"],
): UserExposureHistory {
  const totals: ExposureTotal[] = raw.totals.map((t) => {
    const peak_ppm = Number(t.peak_ppm);
    // The backend emits CO/H2S/VOC labels; narrow to RelGas so the peak carries
    // its REL context. An unknown label keeps null REL context rather than NaN.
    const known = t.gas in NIOSH_REL_PPM;
    const gas = t.gas as RelGas;
    const pct = known ? pctRel(gas, peak_ppm) : null;
    return {
      gas: t.gas,
      ppm_minutes: Number(t.ppm_minutes),
      peak_ppm,
      niosh_rel_ppm: known ? NIOSH_REL_PPM[gas] : null,
      pct_rel: pct,
      over_rel: (pct ?? 0) >= 100,
    };
  });
  return {
    totals,
    incidents: raw.incidents.map((inc) => ({
      incident_id: inc.incident_id,
      dispatched_at: inc.dispatched_at,
      ppm_minutes_total: Number(inc.ppm_minutes_total),
    })),
  };
}
